import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
/* eslint-disable react/no-unknown-property -- project-standard Cypress selectors */
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterListIcon from "@mui/icons-material/FilterList";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { realOrSimulatedNow } from "../../shift";
import { APPOINTMENT_STATUS_OPTIONS, statusMetadata } from "../ui/status_metadata";
import { appointmentUnavailableToPeer } from "./appointment_request_simple_display";
import ThreeDayTimeline from "./appointment_requests_three_day_timeline";
import AppointmentRequestsDayAgenda from "./appointment_requests_day_agenda";
import {
  appointmentClient,
  appointmentDateKey,
  appointmentStableKey,
  appointmentTime,
  compactAppointmentTime,
  dateFromKey,
  formatDateKey,
  hasTimeOffOnDay,
  sortAppointments
} from "./appointment_requests_calendar_utils";

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => [
  new Date(2000, index, 1).toLocaleDateString("en-US", { month: "long" }),
  index + 1
]);

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const sixWeekCalendarDays = (year, monthIndex) => {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const firstVisibleDay = new Date(firstOfMonth);
  firstVisibleDay.setDate(firstVisibleDay.getDate() - firstVisibleDay.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(firstVisibleDay);
    date.setDate(firstVisibleDay.getDate() + index);
    return date;
  });
};

const normalizedOptions = (options, fallback) => {
  if (!Array.isArray(options) || options.length === 0) return fallback;
  return options.map((option) => {
    if (Array.isArray(option)) return option;
    return [option.label, option.value];
  });
};

const selectedOptionLabel = (options, selectedValue) => {
  const selectedOption = options.find(([, value]) => String(value) === String(selectedValue));
  return selectedOption ? selectedOption[0] : String(selectedValue);
};

const AppointmentsRequestsCalendar = ({
  appointment_requests,
  user_data,
  month,
  year,
  peer_schedules,
  highlight_appointment_id,
  filters,
  peer_options,
  pickup_options,
  unit_options
}) => {
  const appointmentRequests = appointment_requests || [];
  const userData = user_data || {};
  const isMobile = useIsMobile();
  const now = realOrSimulatedNow();
  const parsedMonth = parseInt(month, 10);
  const parsedYear = parseInt(year, 10);
  const calendarMonth = parsedMonth >= 1 && parsedMonth <= 12 ? parsedMonth - 1 : now.getMonth();
  const calendarYear = Number.isNaN(parsedYear) ? now.getFullYear() : parsedYear;
  const yearOptions = Array.from({ length: 9 }, (_, index) => calendarYear - 4 + index);
  const activeFilters = filters || {};
  const selectedFilter = activeFilters.what_to_show || "all";
  const selectedPickup = activeFilters.pickup_location || "all";
  const selectedUnit = activeFilters.unit_location || "all";
  const selectedStatus = activeFilters.status || "all";
  const initialDay = dateFromKey(activeFilters.day) ? activeFilters.day : null;
  const requestedCalendarView = (() => {
    if (activeFilters.calendar_view) return activeFilters.calendar_view;
    if (typeof window === "undefined") return null;
    return new URL(window.location.href).searchParams.get("calendar_view");
  })();
  const initialThreeDayStart = (() => {
    const requestedStart =
      typeof window === "undefined"
        ? null
        : dateFromKey(new URL(window.location.href).searchParams.get("start_day"));
    if (requestedStart) return formatDateKey(requestedStart);

    const selectedDate = dateFromKey(initialDay);
    if (selectedDate) {
      selectedDate.setDate(selectedDate.getDate() - 1);
      return formatDateKey(selectedDate);
    }

    if (now.getFullYear() === calendarYear && now.getMonth() === calendarMonth)
      return formatDateKey(now);
    return formatDateKey(new Date(calendarYear, calendarMonth, 1));
  })();
  const [selectedDayKey, setSelectedDayKey] = useState(initialDay);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileCalendarView, setMobileCalendarView] = useState(
    requestedCalendarView === "month" ? "month" : "three_day"
  );
  const [threeDayStartKey, setThreeDayStartKey] = useState(initialThreeDayStart);
  const dayWasPushed = useRef(false);

  const calendarDays = useMemo(
    () => sixWeekCalendarDays(calendarYear, calendarMonth),
    [calendarMonth, calendarYear]
  );
  const threeDayCalendarDays = useMemo(() => {
    const start = dateFromKey(threeDayStartKey) || new Date(calendarYear, calendarMonth, 1);
    return Array.from({ length: 3 }, (_, index) => {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      return day;
    });
  }, [calendarMonth, calendarYear, threeDayStartKey]);

  const appointmentsByDay = useMemo(() => {
    return appointmentRequests.reduce((grouped, appointment) => {
      const dateKey = appointmentDateKey(appointment);
      if (!dateKey) return grouped;
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(appointment);
      return grouped;
    }, {});
  }, [appointmentRequests]);

  const sortedAppointmentsByDay = useMemo(() => {
    return Object.entries(appointmentsByDay).reduce((grouped, [dateKey, appointments]) => {
      grouped[dateKey] = sortAppointments(appointments);
      return grouped;
    }, {});
  }, [appointmentsByDay]);

  const monthPrefix = `${calendarYear}-${`${calendarMonth + 1}`.padStart(2, "0")}`;
  const monthAppointments = appointmentRequests.filter((appointment) =>
    (appointmentDateKey(appointment) || "").startsWith(monthPrefix)
  );
  const scheduledDays = new Set(monthAppointments.map(appointmentDateKey)).size;
  const unmatchedCount = monthAppointments.filter(
    (appointment) => appointment.status === "unmatched"
  ).length;
  const unavailableToViewerCount = monthAppointments.filter(
    (appointment) =>
      userData.account_type === "Peer" && appointmentUnavailableToPeer(appointment, userData.id)
  ).length;
  const selectedDay = selectedDayKey ? dateFromKey(selectedDayKey) : null;
  const selectedDayAppointments = selectedDayKey
    ? sortedAppointmentsByDay[selectedDayKey] || []
    : [];
  const highlightedId =
    highlight_appointment_id === null || highlight_appointment_id === undefined
      ? null
      : String(highlight_appointment_id);

  const peerOptions = normalizedOptions(peer_options, [
    ["All peers & unmatched", "all"],
    ["Unmatched only", "unmatched"]
  ]);
  const pickupOptions = normalizedOptions(pickup_options, [["All pickup locations", "all"]]);
  const unitOptions = normalizedOptions(unit_options, [["All units", "all"]]);
  const activeFilterDetails = [
    {
      key: "peer",
      label: "Peer",
      value: selectedFilter,
      displayValue: selectedOptionLabel(peerOptions, selectedFilter)
    },
    {
      key: "pickup",
      label: "Pickup",
      value: selectedPickup,
      displayValue: selectedOptionLabel(pickupOptions, selectedPickup)
    },
    {
      key: "unit",
      label: "Unit",
      value: selectedUnit,
      displayValue: selectedOptionLabel(unitOptions, selectedUnit)
    },
    {
      key: "status",
      label: "Status",
      value: selectedStatus,
      displayValue: selectedOptionLabel(APPOINTMENT_STATUS_OPTIONS, selectedStatus)
    }
  ].filter(({ value }) => value && value !== "all");

  useEffect(() => {
    const handleHistoryChange = () => {
      const url = new URL(window.location.href);
      const urlDay = url.searchParams.get("day");
      const urlStart = url.searchParams.get("start_day");
      setSelectedDayKey(dateFromKey(urlDay) ? urlDay : null);
      if (dateFromKey(urlStart)) setThreeDayStartKey(urlStart);
      dayWasPushed.current = false;
    };
    window.addEventListener("popstate", handleHistoryChange);
    return () => window.removeEventListener("popstate", handleHistoryChange);
  }, []);

  useEffect(() => {
    if (!highlightedId) return;
    const highlightedAppointment = appointmentRequests.find(
      (appointment) =>
        String(appointment.occurrence_key || appointment.id) === highlightedId ||
        String(appointment.id) === highlightedId
    );
    const dateKey = highlightedAppointment && appointmentDateKey(highlightedAppointment);
    if (!dateKey) return;

    const handle = window.requestAnimationFrame(() => {
      const target = document.querySelector(`[data-date-key="${dateKey}"]`);
      if (target && target.scrollIntoView)
        target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    });
    return () => window.cancelAnimationFrame(handle);
  }, [appointmentRequests, highlightedId]);

  const updateUrl = (changes, { clearDay = false } = {}) => {
    const url = new URL(window.location.href);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "" || value === "all")
        url.searchParams.delete(key);
      else url.searchParams.set(key, value);
    });
    if (clearDay) {
      url.searchParams.delete("day");
      url.searchParams.delete("highlight_appointment_id");
    }
    window.location.assign(url.toString());
  };

  const openDay = (dateKey) => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("day") !== dateKey) {
      url.searchParams.set("day", dateKey);
      window.history.pushState({ peerDashboardDay: dateKey }, "", url.toString());
      dayWasPushed.current = true;
    }
    setSelectedDayKey(dateKey);
  };

  const closeDay = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("day");
    window.history.replaceState(
      { ...(window.history.state || {}), peerDashboardDay: null },
      "",
      url.toString()
    );
    dayWasPushed.current = false;
    setSelectedDayKey(null);
  };

  const changeMobileCalendarView = (view) => {
    const url = new URL(window.location.href);
    url.searchParams.set("calendar_view", view);
    window.history.replaceState(window.history.state, "", url.toString());
    setMobileCalendarView(view);
  };

  const changeThreeDayWindow = (dayOffset) => {
    const currentStart = dateFromKey(threeDayStartKey) || new Date(calendarYear, calendarMonth, 1);
    const destination = new Date(currentStart);
    destination.setDate(currentStart.getDate() + dayOffset);
    const destinationKey = formatDateKey(destination);
    const url = new URL(window.location.href);
    url.searchParams.set("start_day", destinationKey);

    if (destination.getMonth() !== calendarMonth || destination.getFullYear() !== calendarYear) {
      url.searchParams.set("month", destination.getMonth() + 1);
      url.searchParams.set("year", destination.getFullYear());
      url.searchParams.delete("day");
      url.searchParams.delete("highlight_appointment_id");
      window.location.assign(url.toString());
      return;
    }

    window.history.replaceState(window.history.state, "", url.toString());
    setThreeDayStartKey(destinationKey);
  };

  const changeMonth = (monthOffset) => {
    const destination = new Date(calendarYear, calendarMonth + monthOffset, 1);
    updateUrl(
      {
        month: destination.getMonth() + 1,
        year: destination.getFullYear(),
        start_day: formatDateKey(destination)
      },
      { clearDay: true }
    );
  };

  const goToToday = () =>
    updateUrl(
      {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        start_day: formatDateKey(now)
      },
      { clearDay: true }
    );

  const filterCount = [selectedFilter, selectedPickup, selectedUnit, selectedStatus].filter(
    (value) => value && value !== "all"
  ).length;

  const calendarTitle = (() => {
    if (selectedFilter === "unmatched") return "Unmatched appointments";
    if (String(selectedFilter) === String(userData.id)) return "My appointments";
    const peerOption = peerOptions.find((option) => String(option[1]) === String(selectedFilter));
    return peerOption && selectedFilter !== "all"
      ? `${peerOption[0]}'s appointments`
      : "All appointments";
  })();

  const renderAppointmentPreview = (appointment, index, dateKey, fullDate, variant) => {
    const status = statusMetadata(appointment.status);
    const recurring = appointment.is_recurring || appointment.recurring;
    const unavailableToViewer =
      userData.account_type === "Peer" && appointmentUnavailableToPeer(appointment, userData.id);
    const previewStatusLabel = unavailableToViewer
      ? `${status.label}, unavailable to you`
      : status.label;
    const visibleTime =
      variant === "month" && isMobile
        ? compactAppointmentTime(appointment)
        : appointmentTime(appointment);

    return (
      <button
        key={appointmentStableKey(appointment, index)}
        type="button"
        className={`peer_dashboard_appointment_preview peer_dashboard_appointment_preview--${variant} ${
          status.className
        }${unavailableToViewer ? " is-unavailable" : ""}`}
        aria-label={`${appointmentTime(appointment)}, ${appointmentClient(
          appointment
        )}, ${previewStatusLabel}${recurring ? ", recurring" : ""}. Open ${fullDate}.`}
        onClick={() => openDay(dateKey)}
        explorator_test_id={`peer-dashboard-appointment-preview-${appointmentStableKey(
          appointment,
          index
        )}`}
        title={unavailableToViewer ? "Unavailable to you" : undefined}
      >
        <span className="peer_dashboard_preview_time">{visibleTime}</span>
        <span className="peer_dashboard_preview_client">{appointmentClient(appointment)}</span>
        {recurring && (
          <span className="peer_dashboard_preview_recurring" title="Recurring series">
            ↻
          </span>
        )}
        <span
          className={`peer_dashboard_preview_status${unavailableToViewer ? " is-unavailable" : ""}`}
          aria-hidden="true"
        >
          {status.symbol}
        </span>
      </button>
    );
  };

  return (
    <section
      className="ccw-surface peer_dashboard_calendar_panel"
      aria-labelledby="peer-calendar-title"
    >
      <div className="peer_dashboard_calendar_toolbar">
        <div className="peer_dashboard_calendar_intro">
          <p className="peer_dashboard_eyebrow">Appointment requests</p>
          <h2 id="peer-calendar-title" className="peer_dashboard_calendar_heading">
            {calendarTitle}
          </h2>
          <p className="peer_dashboard_calendar_instruction">
            Select a date to review full trip details and actions.
          </p>
        </div>
        <div className="peer_dashboard_calendar_sticky_controls">
          <div className="peer_dashboard_period_controls" aria-label="Calendar period">
            <button
              type="button"
              className="ccw-button ccw-button--icon peer_dashboard_icon_button"
              aria-label="Previous month"
              explorator_test_id="peer-dashboard-previous-month"
              onClick={() => changeMonth(-1)}
            >
              <ChevronLeftIcon fontSize="small" />
            </button>
            <label className="peer_dashboard_visually_hidden" htmlFor="peer-calendar-month">
              Month
            </label>
            <select
              id="peer-calendar-month"
              explorator_test_id="peer-dashboard-month-select"
              className="ccw-select peer_dashboard_control"
              value={calendarMonth + 1}
              onChange={(event) => {
                const destination = new Date(calendarYear, parseInt(event.target.value, 10) - 1, 1);
                updateUrl(
                  {
                    month: event.target.value,
                    year: calendarYear,
                    start_day: formatDateKey(destination)
                  },
                  { clearDay: true }
                );
              }}
            >
              {MONTH_OPTIONS.map(([label, value]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <label className="peer_dashboard_visually_hidden" htmlFor="peer-calendar-year">
              Year
            </label>
            <select
              id="peer-calendar-year"
              explorator_test_id="peer-dashboard-year-select"
              className="ccw-select peer_dashboard_control peer_dashboard_year_control"
              value={calendarYear}
              onChange={(event) => {
                const destination = new Date(parseInt(event.target.value, 10), calendarMonth, 1);
                updateUrl(
                  {
                    month: calendarMonth + 1,
                    year: event.target.value,
                    start_day: formatDateKey(destination)
                  },
                  { clearDay: true }
                );
              }}
            >
              {yearOptions.map((yearOption) => (
                <option key={yearOption} value={yearOption}>
                  {yearOption}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="ccw-button ccw-button--icon peer_dashboard_icon_button"
              aria-label="Next month"
              explorator_test_id="peer-dashboard-next-month"
              onClick={() => changeMonth(1)}
            >
              <ChevronRightIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="ccw-button ccw-button--primary peer_dashboard_today_button"
              onClick={goToToday}
              explorator_test_id="peer-dashboard-today"
            >
              Today
            </button>
          </div>
          <div
            className="ccw-segmented peer_dashboard_mobile_view_switch"
            role="group"
            aria-label="Mobile calendar view"
          >
            <button
              type="button"
              className={`ccw-segmented__button ${
                mobileCalendarView === "three_day" ? "is-active" : ""
              }`}
              aria-pressed={mobileCalendarView === "three_day"}
              explorator_test_id="peer-dashboard-view-three-day"
              onClick={() => changeMobileCalendarView("three_day")}
            >
              3 days
            </button>
            <button
              type="button"
              className={`ccw-segmented__button ${
                mobileCalendarView === "month" ? "is-active" : ""
              }`}
              aria-pressed={mobileCalendarView === "month"}
              explorator_test_id="peer-dashboard-view-month"
              onClick={() => changeMobileCalendarView("month")}
            >
              Month
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="ccw-button peer_dashboard_mobile_filter_toggle"
        aria-expanded={mobileFiltersOpen}
        aria-controls="peer-dashboard-filters"
        explorator_test_id="peer-dashboard-filter-toggle"
        onClick={() => setMobileFiltersOpen((open) => !open)}
      >
        <FilterListIcon fontSize="small" /> Filters
        {filterCount > 0 && <span>{filterCount}</span>}
      </button>

      <div
        id="peer-dashboard-filters"
        className={`peer_dashboard_filter_grid ${mobileFiltersOpen ? "is-open" : ""}`}
      >
        <label>
          <span>Peer / unmatched</span>
          <select
            className="ccw-select peer_dashboard_control"
            value={selectedFilter}
            explorator_test_id="peer-dashboard-peer-filter"
            onChange={(event) => updateUrl({ what_to_show: event.target.value })}
          >
            {peerOptions.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Pickup</span>
          <select
            className="ccw-select peer_dashboard_control"
            value={selectedPickup}
            explorator_test_id="peer-dashboard-pickup-filter"
            onChange={(event) => updateUrl({ pickup_location: event.target.value })}
          >
            {pickupOptions.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Unit</span>
          <select
            className="ccw-select peer_dashboard_control"
            value={selectedUnit}
            explorator_test_id="appointment-unit-location-filter"
            onChange={(event) => updateUrl({ unit_location: event.target.value })}
          >
            {unitOptions.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select
            className="ccw-select peer_dashboard_control"
            value={selectedStatus}
            explorator_test_id="peer-dashboard-status-filter"
            onChange={(event) => updateUrl({ status: event.target.value })}
          >
            {APPOINTMENT_STATUS_OPTIONS.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="ccw-button ccw-button--quiet peer_dashboard_clear_filters"
          disabled={filterCount === 0}
          explorator_test_id="peer-dashboard-clear-filters"
          onClick={() =>
            updateUrl({
              what_to_show: null,
              pickup_location: null,
              unit_location: null,
              status: null
            })
          }
        >
          Clear filters
        </button>
      </div>

      <div className="peer_dashboard_calendar_meta">
        <p>
          <strong>{monthAppointments.length}</strong> appointments ·{" "}
          <strong>{scheduledDays}</strong> scheduled days · <strong>{unmatchedCount}</strong>{" "}
          unmatched
          {unavailableToViewerCount > 0 ? (
            <>
              {" "}
              · <strong>{unavailableToViewerCount}</strong> unavailable to you
            </>
          ) : null}
        </p>
        <details className="peer_dashboard_calendar_key">
          <summary>Calendar key</summary>
          <div>
            <span className="ccw-status ccw-status--matched peer_dashboard_key_swatch status-matched">
              ✓ Matched
            </span>
            <span className="ccw-status ccw-status--unmatched peer_dashboard_key_swatch status-unmatched">
              ○ Unmatched
            </span>
            <span className="ccw-status ccw-status--denied peer_dashboard_key_swatch status-denied">
              × Denied
            </span>
            <span className="ccw-status ccw-status--update peer_dashboard_key_swatch status-needs-update">
              ! Needs update
            </span>
            {userData.account_type === "Peer" && (
              <span className="ccw-status ccw-status--unavailable peer_dashboard_key_swatch status-unavailable">
                — Unavailable to you
              </span>
            )}
            <span className="peer_dashboard_key_swatch is-time-off">— Time off</span>
            <span className="peer_dashboard_key_swatch is-conflict">⚠ Conflict</span>
          </div>
        </details>
      </div>

      {(!isMobile || mobileCalendarView === "month") && (
        <div
          className="peer_dashboard_calendar_grid"
          role="region"
          aria-label={`${calendarTitle}, ${MONTH_OPTIONS[calendarMonth][0]} ${calendarYear}, month view`}
        >
          {WEEKDAY_LABELS.map((weekday) => (
            <div key={weekday} className="peer_dashboard_weekday">
              {weekday}
            </div>
          ))}
          {calendarDays.map((day) => {
            const dateKey = formatDateKey(day);
            const appointments = sortedAppointmentsByDay[dateKey] || [];
            const timeOff = hasTimeOffOnDay(peer_schedules, day);
            const conflict = timeOff && appointments.length > 0;
            const outsideMonth = day.getMonth() !== calendarMonth;
            const today = formatDateKey(now) === dateKey;
            const selected = selectedDayKey === dateKey;
            const dayHasHighlight =
              highlightedId &&
              appointments.some(
                (appointment) =>
                  String(appointment.occurrence_key || appointment.id) === highlightedId ||
                  String(appointment.id) === highlightedId
              );
            const fullDate = day.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric"
            });
            const cellClasses = ["peer_dashboard_day_cell"];
            if (outsideMonth) cellClasses.push("is-outside-month");
            if (today) cellClasses.push("is-today");
            if (timeOff) cellClasses.push("is-time-off");
            if (conflict) cellClasses.push("is-conflict");
            if (selected) cellClasses.push("is-selected");
            if (dayHasHighlight) cellClasses.push("has-highlight");

            return (
              <div
                key={dateKey}
                className={cellClasses.join(" ")}
                data-date-key={dateKey}
                onClick={(event) => {
                  if (event.target.closest("button, a, input, select")) return;
                  openDay(dateKey);
                }}
              >
                <div className="peer_dashboard_day_header">
                  <button
                    type="button"
                    className="peer_dashboard_date_button"
                    aria-label={`${fullDate}, ${appointments.length} appointment${
                      appointments.length === 1 ? "" : "s"
                    }${timeOff ? (conflict ? ", time-off conflict" : ", time off") : ""}`}
                    aria-current={today ? "date" : undefined}
                    explorator_test_id={`peer-dashboard-date-${dateKey}`}
                    onClick={() => openDay(dateKey)}
                  >
                    {day.getDate()}
                  </button>
                  {conflict ? (
                    <span className="peer_dashboard_day_availability">⚠ Conflict</span>
                  ) : timeOff ? (
                    <span className="peer_dashboard_day_availability">— Time off</span>
                  ) : null}
                </div>

                <div className="peer_dashboard_day_appointments">
                  {appointments.length > 0 && (
                    <button
                      type="button"
                      className="peer_dashboard_month_count"
                      aria-label={`View all ${appointments.length} appointments on ${fullDate}`}
                      explorator_test_id={`peer-dashboard-count-${dateKey}`}
                      onClick={() => openDay(dateKey)}
                    >
                      <span aria-hidden="true">≡</span> {appointments.length}
                    </button>
                  )}
                  {appointments
                    .slice(0, 2)
                    .map((appointment, index) =>
                      renderAppointmentPreview(appointment, index, dateKey, fullDate, "month")
                    )}
                  {appointments.length === 0 && !outsideMonth && (
                    <span className="peer_dashboard_empty_day">No appointments</span>
                  )}
                  {appointments.length > 2 && (
                    <button
                      type="button"
                      className="peer_dashboard_more_button"
                      aria-label={`View all ${appointments.length} appointments on ${fullDate}`}
                      explorator_test_id={`peer-dashboard-more-${dateKey}`}
                      onClick={() => openDay(dateKey)}
                    >
                      +{appointments.length - 2} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isMobile && mobileCalendarView === "three_day" && (
        <div
          className="peer_dashboard_three_day_grid"
          role="region"
          aria-label={`${calendarTitle}, ${MONTH_OPTIONS[calendarMonth][0]} ${calendarYear}, three-day view`}
        >
          <ThreeDayTimeline
            days={threeDayCalendarDays}
            appointmentsByDay={sortedAppointmentsByDay}
            peerSchedules={peer_schedules}
            userData={userData}
            selectedDayKey={selectedDayKey}
            onOpenDay={openDay}
            onPrevious={() => changeThreeDayWindow(-3)}
            onNext={() => changeThreeDayWindow(3)}
          />
        </div>
      )}

      <div
        className="peer_dashboard_calendar_live peer_dashboard_visually_hidden"
        aria-live="polite"
      >
        Showing {MONTH_OPTIONS[calendarMonth][0]} {calendarYear}
      </div>

      <AppointmentRequestsDayAgenda
        day={selectedDay}
        appointments={selectedDayAppointments}
        userData={userData}
        isMobile={isMobile}
        activeFilters={activeFilterDetails}
        onClose={closeDay}
      />
    </section>
  );
};

AppointmentsRequestsCalendar.propTypes = {
  appointment_requests: PropTypes.array,
  user_data: PropTypes.object,
  month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  peer_schedules: PropTypes.array,
  highlight_appointment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  filters: PropTypes.object,
  peer_options: PropTypes.array,
  pickup_options: PropTypes.array,
  unit_options: PropTypes.array
};

AppointmentsRequestsCalendar.defaultProps = {
  appointment_requests: [],
  user_data: {},
  peer_schedules: [],
  highlight_appointment_id: null,
  filters: {},
  peer_options: [],
  pickup_options: [],
  unit_options: []
};

export default AppointmentsRequestsCalendar;
